/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useEffect, useContext } from "react";
import { useToasts } from "react-toast-notifications";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { getFilePlugin } from "@react-pdf-viewer/get-file";
import { Dashboard2Context } from "./dashboard2";
import { MFRUploadDialogContext } from "../components/MFRUploadDialog";
import { DialogContext } from "../components/Dialog";
import { FullPageLoadingContext } from "../components/FullPageLoading";
import { LayoutContext } from "../components/layout";
import fetchHelper from "../services/fetchHelper";
import { downloadAsBlob, getSafeIrFilename } from "../utils/helpers";
import pdfContext from "./pdfContext";
import savingspinner from "../assets/esign-ui-icons/saving-ico-white.svg";
import { REQUEST_TYPE_PDF } from "../components/Modals/constants";

import "./MFRReport.css";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const cancelSaveCtrl = new AbortController();

const MFRReport = ({ mfrReportPdf, getMFRReportPDF, pdfZoomScale, attachmentSize }) => {
  const { showFullPageLoading } = useContext(FullPageLoadingContext);
  const { showDialog, hideDialog, setLoadingDialog } = useContext(DialogContext);
  const { showMFRUploadDialog, hideMFRUploadDialog } = useContext(MFRUploadDialogContext);
  const { userInfo } = useContext(Dashboard2Context);
  const { headerDetails, updateHeaderDetails } = useContext(LayoutContext);
  const [isLoading] = useState(false);
  const [downloadingPreReport, setDownloadingPreReport] = useState(null);
  const { i: interrogationId } = useParams();
  const { addToast } = useToasts();
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { jumpToPage } = pageNavigationPluginInstance;
  const { setOffSet, setNumberOfPages } = useContext(pdfContext);
  const attachmentSizeRef = useRef(attachmentSize);

  const getFilePluginInstance = getFilePlugin({
    fileNameGenerator: () => getFilename(),
  });

  const { DownloadButton: Download } = getFilePluginInstance;

  useEffect(() => {
    if (!mfrReportPdf) {
      (async () => {
        await handleOnRefresh();
      })();
    }
  }, [mfrReportPdf]);

  const getAttachmentSize = async () => {
    try {
      const data = await fetchHelper.get({
        url: `/patientmanagement/rest/pdfReport/interrogation/${interrogationId}/attachment/pagesCount`,
      });
      attachmentSizeRef.current = data.pageCount;
    } catch (err) {
      console.log(err);
      attachmentSizeRef.current = 0;
    }
  };

  useEffect(() => {
    if (headerDetails.currentPdfPage) jumpToPage(headerDetails.currentPdfPage - 1);
    resetCurrentPageNumber();
  }, [headerDetails.currentPdfPage && headerDetails.currentPdfPage !== 0]);

  const getFilename = () => getSafeIrFilename(headerDetails.patient, interrogationId);

  const setPreview = async () => {
    try {
      showFullPageLoading(true);
      await getMFRReportPDF(REQUEST_TYPE_PDF.DEFAULT, 1, attachmentSizeRef.current);
    } catch (err) {
      addToast(err.message, {
        appearance: "error",
      });
    } finally {
      showFullPageLoading(false);
    }
  };

  const handleOnRefresh = async () => {
    await getAttachmentSize();
    await setPreview();
  };

  const onPressSave = async (e, base64, name, file) => {
    let addAttachmentResponse;
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("file1", file);
      addAttachmentResponse = await fetchHelper.post(
        `/patientmanagement/rest/interrogations/${interrogationId}/addAttachment`,
        formData,
        null,
        false,
        {
          signal: cancelSaveCtrl.signal,
        }
      );
    } catch (err) {
      console.error(err);
    } finally {
      if (addAttachmentResponse?.correlationId) {
        await getPreliminaryGenerationStatus(addAttachmentResponse.correlationId);
      } else {
        await generatePreliminary();
      }
      await handleOnRefresh();
      hideMFRUploadDialog();
    }
  };
  const onPressCancel = (e) => {
    e.preventDefault();
    cancelSaveCtrl.abort();
    hideMFRUploadDialog();
  };

  const resetCurrentPageNumber = () => {
    let updatedObject;
    const headerDetailsCopy = { ...headerDetails };
    headerDetailsCopy.currentPdfPage = 0;
    updatedObject = headerDetailsCopy;
    updateHeaderDetails(updatedObject);
  };

  const fetchPatient = () =>
    fetchHelper.get({ url: `/patientmanagement/rest/patients/${userInfo.patient.id}` });

  const deleteAttachments = async () => {
    let pdfGenerationId = null;
    try {
      setLoadingDialog(true);
      pdfGenerationId = await fetchHelper.put(
        `/patientmanagement/rest/interrogations/${interrogationId}/removeAttachments`
      );
      await fetchPatient();
    } catch (err) {
      console.error(err);
    } finally {
      if (pdfGenerationId?.response) {
        await getPreliminaryGenerationStatus(pdfGenerationId.response);
      } else {
        await handleOnRefresh();
      }
      hideDialog();
      setLoadingDialog(false);
    }
  };

  const generatePreliminary = async () => {
    let pdfGenerationId = null;
    try {
      showFullPageLoading(true);

      const response = await fetchHelper.put(
        `/patientmanagement/rest/interrogations/${interrogationId}/generatePreReport`,
        {
          id: interrogationId,
        }
      );
      if (response) {
        pdfGenerationId = response.response;
      }

      await fetchPatient();
    } catch (err) {
      console.error(err);
    } finally {
      if (pdfGenerationId) {
        await getPreliminaryGenerationStatus(pdfGenerationId, REQUEST_TYPE_PDF.PRELIMINARY);
      } else {
        showFullPageLoading(false);
      }
    }
  };

  const getPreliminaryGenerationStatus = async (pdfGenerationId, requestTypePDF) => {
    try {
      const response = await fetchHelper.get({
        url: `/patientmanagement/rest/PdfGenerator/${pdfGenerationId}/status`,
      });
      if (response.status === "SUCCESSFUL") {
        addToast("The PDF was successfully generated!", {
          appearance: "success",
        });
      } else {
        addToast(
          "There was a problem while generating the pdf for the interrogation id " + interrogationId + ".",
          {
            appearance: "error",
          }
        );
      }
    } catch (err) {
      addToast(err.message, {
        appearance: "error",
      });
    } finally {
      await getAttachmentSize();
      await getMFRReportPDF(requestTypePDF);
      showFullPageLoading(false);
    }
  };

  const downloadReport = async () => {
    setDownloadingPreReport("Preliminary Report");
    const pdfQueueMicroservice = await fetchHelper.getPdfQueueStatus(headerDetails.location.id);
    let extraParams = pdfQueueMicroservice ? `/${headerDetails.interrogationStatus}` : "";

    await downloadAsBlob(
      `/patientmanagement/rest/interrogations/${interrogationId}/download${extraParams}`,
      `${getFilename()}.pdf`
    );

    setDownloadingPreReport(null);
  };

  let mfrMainClass = "mfr-main";
  if (headerDetails.interrogationStatus === "FINAL") {
    mfrMainClass += " mfr--final";
  }

  const renderToolbar = (Toolbar) => (
    <Toolbar>
      {(slots) => {
        const {
          CurrentPageInput,
          EnterFullScreen,
          GoToNextPage,
          GoToPreviousPage,
          NumberOfPages,
          Print,
          ShowSearchPopover,
          Zoom,
          ZoomIn,
          ZoomOut,
        } = slots;
        return (
          <div className="pdf-viewer">
            <ShowSearchPopover title="Search" />
            <div className="pdf-viewer-options page-number ">
              <ZoomOut title="Zoom out" />
              <Zoom />
              <ZoomIn title="Zoom in" />
              <span className="divider" />
              <CurrentPageInput /> / <NumberOfPages />
              <GoToPreviousPage title="Previous page" />
              <GoToNextPage title="Next page" />
            </div>
            <div className="pdf-viewer-options">
              <EnterFullScreen title="Full screen" />
              <Download title="Download" />
              <Print title="Print" />
            </div>
          </div>
        );
      }}
    </Toolbar>
  );
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar,
    sidebarTabs: (defaultTabs) => [defaultTabs[0]],
  });

  const onDocumentLoad = (e) => {
    const numberOfPages = e.doc.numPages;
    setNumberOfPages(numberOfPages);
    const calculatedOffSet = numberOfPages - attachmentSizeRef.current;
    setOffSet(calculatedOffSet);
  };

  const resetTotalPageNumber = () => {
    setNumberOfPages(0);
    setOffSet(0);
  };

  return (
    <>
      <div id="mfr-report-container" className="tab-content mfr-rpt">
        <div className={mfrMainClass}>
          {userInfo && headerDetails.interrogationStatus !== "FINAL" ? (
            <div className="show-pgs no-print">
              <div className="mng-pgs">
                <ul>
                  <li>
                    <button
                      className="btn btn-teal btn-add-remove-pages"
                      disabled={isLoading}
                      onClick={(e) => {
                        e.preventDefault();
                        showMFRUploadDialog({ onPressSave, onPressCancel });
                      }}
                    >
                      <i className="ti ti-plus"></i>
                      <span>Add New</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className="btn btn-teal btn-add-remove-pages"
                      onClick={(e) => {
                        e.preventDefault();
                        showDialog({
                          message: "Are you sure you want to delete all the attachments? ",
                          buttons: [
                            { label: "Cancel", onClick: () => hideDialog() },
                            {
                              label: "Delete",
                              type: "success",
                              onClick: () => {
                                deleteAttachments();
                              },
                            },
                          ],
                        });
                      }}
                    >
                      <i className="ti ti-minus"></i>
                      <span>Delete</span>
                    </button>
                  </li>
                  <li>
                    <button className="btn btn-teal btn-add-remove-pages report" onClick={handleOnRefresh}>
                      <i className="ti ti-reload"></i>
                      <span>Refresh</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className="btn btn-teal btn-add-remove-pages report btn-y2x"
                      type="button"
                      onClick={generatePreliminary}
                    >
                      <i className="ti ti-reload"></i>
                      <span>
                        Generate
                        <br />
                        Preliminary Report
                      </span>
                    </button>
                  </li>
                  <li>
                    <button
                      className="btn btn-teal btn-add-remove-pages report btn-y2x"
                      onClick={downloadReport}
                      disabled={isLoading}
                    >
                      {downloadingPreReport == "Preliminary Report" ? (
                        <img id="loading-spinner" src={savingspinner} style={{ width: 15 }} />
                      ) : (
                        <i className="ti ti-download"></i>
                      )}
                      <span>
                        Download
                        <br />
                        Preliminary Report
                      </span>
                    </button>
                  </li>
                </ul>
              </div>
              <button className="btn btn-no-txt btn-teal btn-open-pages">
                <i className="ti ti-column"></i>
              </button>
            </div>
          ) : null}

          {mfrReportPdf && (
            <div
              className="pdf-container"
              id={
                headerDetails.interrogationStatus !== "FINAL" ? "pdf-container-id" : "final-pdf-container-id"
              }
            >
              <Worker workerUrl="/ReactPdfWorker.min.js">
                <Viewer
                  fileUrl={mfrReportPdf}
                  defaultScale={pdfZoomScale}
                  plugins={[defaultLayoutPluginInstance, pageNavigationPluginInstance, getFilePluginInstance]}
                  theme={{
                    theme: "dark",
                  }}
                  onDocumentLoad={onDocumentLoad}
                />
                ;
              </Worker>
            </div>
          )}

          {!mfrReportPdf && (
            <div
              className="empty-pdf-container"
              id={
                headerDetails.interrogationStatus !== "FINAL"
                  ? "empty-pdf-container-id"
                  : "final-empty-pdf-container-id"
              }
            >
              <p>No PDF available. Please attach a document.</p>
              {resetTotalPageNumber()}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MFRReport;
